// Global Instructions Rule Applied!

import { useCallback, useEffect, useState } from 'react'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { fetchScheduleOfInvestments } from '../services/scheduleOfInvestmentsService.ts'

export function useScheduleOfInvestmentsData(
  selectedFundIds: string[],
  selectedDate: string,
  pByFund: boolean,
  pByVintage: boolean,
  pByTranche: boolean,
  options?: { enabled?: boolean }
) {
  const enabled = options?.enabled !== false
  const [data, setData] = useState<Record<string, unknown>[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fundIdsKey = selectedFundIds.slice().sort().join(',')

  useEffect(() => {
    if (!enabled) {
      setData([])
      setError(null)
      setLoading(false)
      return
    }

    let cancelled = false

    const run = async () => {
      setLoading(true)
      setError(null)
      try {
        const accessToken = getAccessToken(store.getState().auth)
        const response = await fetchScheduleOfInvestments(
          selectedFundIds.length > 0 ? selectedFundIds : null,
          selectedDate || undefined,
          pByFund,
          pByVintage,
          pByTranche,
          accessToken
        )

        if (cancelled) return

        if (response.error) {
          setError(response.error)
          setData([])
        } else {
          setData(response.data)
        }
      } catch (e) {
        if (!cancelled) {
          if (process.env.NODE_ENV === 'development') {
            console.error('Error fetching schedule of investments:', e)
          }
          setError(e instanceof Error ? e.message : 'Failed to fetch data')
          setData([])
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    run()
    return () => {
      cancelled = true
    }
  }, [fundIdsKey, selectedDate, enabled, pByFund, pByVintage, pByTranche])

  const refetch = useCallback(async () => {
    if (!enabled) return
    setLoading(true)
    setError(null)
    try {
      const accessToken = getAccessToken(store.getState().auth)
      const response = await fetchScheduleOfInvestments(
        selectedFundIds.length > 0 ? selectedFundIds : null,
        selectedDate || undefined,
        pByFund,
        pByVintage,
        pByTranche,
        accessToken
      )
      if (response.error) {
        setError(response.error)
        setData([])
      } else {
        setData(response.data)
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to fetch data')
      setData([])
    } finally {
      setLoading(false)
    }
  }, [fundIdsKey, selectedDate, enabled, pByFund, pByVintage, pByTranche])

  return { data, loading, error, refetch }
}
